/**
 * Plan comparison table (/pricing). Rows + cells from `PLAN_COMPARISON` in
 * `src/lib/pricing.ts`; labels from `pricing.compare.*`. A real <table> for
 * screen readers, horizontally scrollable on narrow screens.
 */
import { Check, Minus } from 'lucide-react'
import { useTranslation } from '../../lib/i18n'
import { PLANS, PLAN_COMPARISON, PLAN_IDS } from '../../lib/pricing'
import type { ComparisonCell } from '../../lib/pricing'

export function PlanComparison() {
  const { t, dict } = useTranslation()

  function renderCell(cell: ComparisonCell) {
    if (cell === true) {
      return (
        <span className="compare-yes">
          <Check size={16} strokeWidth={3} aria-hidden="true" />
          <span className="sr-only">{t('pricing.compare.included')}</span>
        </span>
      )
    }
    if (cell === false) {
      return (
        <span className="compare-no">
          <Minus size={16} aria-hidden="true" />
          <span className="sr-only">{t('pricing.compare.notIncluded')}</span>
        </span>
      )
    }
    return <span className="compare-value">{dict.pricing.compare.values[cell]}</span>
  }

  return (
    <section className="plan-compare" aria-labelledby="plan-compare-title">
      <div className="section-heading">
        <span className="eyebrow">{t('pricing.compare.eyebrow')}</span>
        <h2 id="plan-compare-title">{t('pricing.compare.title')}</h2>
        <p>{t('pricing.compare.text')}</p>
      </div>
      <div className="plan-compare-scroll" tabIndex={0} role="region" aria-labelledby="plan-compare-title">
        <table className="plan-compare-table">
          <thead>
            <tr>
              <th scope="col">{t('pricing.compare.featureHeader')}</th>
              {PLAN_IDS.map((planId) => (
                <th
                  key={planId}
                  scope="col"
                  className={PLANS[planId].recommended ? 'is-recommended' : undefined}
                >
                  {dict.pricing.plans[planId].name}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {PLAN_COMPARISON.map((row) => (
              <tr key={row.key}>
                <th scope="row">{dict.pricing.compare.rows[row.key]}</th>
                {PLAN_IDS.map((planId) => (
                  <td
                    key={planId}
                    className={PLANS[planId].recommended ? 'is-recommended' : undefined}
                  >
                    {renderCell(row.cells[planId])}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  )
}
